import {
	createContext,
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';
import { gql } from '@apollo/client';
import {
	Issue,
	clone,
	getAppToken,
	getUserRole,
	mapIssuesNonImplementedFeatures,
	getImagesFromIssue,
} from '../../../constants';
import {
	GetIssuesResponse,
	GetIssuesArgs,
	sortIssuesReleaseDate,
} from '../../../hooks/use-issue';
import { AppContext } from '../../../App.context';
import { AUTH_KEY } from '../../../views/home/auth/Authentication.hooks';
import config from '../../../config';

export const HomeContext = createContext<HomeContextProps>({
	isLoading: true,
	issues: [],
	search: '',
	setSearch: () => {},
	sortBy: 'date-desc',
	setSortBy: () => {},
});

interface HomeContextProps {
	isLoading: boolean;
	issues: Array<Issue>;
	search: string;
	setSearch: Dispatch<SetStateAction<string>>;
	sortBy: SortType;
	setSortBy: Dispatch<SetStateAction<SortType>>;
	onClickLogOut?: () => void;
}

export type SortType = 'alpha-asc' | 'alpha-desc' | 'date-asc' | 'date-desc';

export function useGetIssues(): GetIssuesResponse {
	const { appKey, apolloClient, onlineStatus, db, kv, sw } =
		useContext(AppContext);

	const [issues, setIssues] = useState<Array<Issue>>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (onlineStatus === null) {
			return;
		}
		if (isLoading === false) {
			return;
		}
		if (!db) {
			return;
		}

		if (!localStorage.getItem(AUTH_KEY)) {
			return;
		}

		const props = {
			apolloClient,
			appKey,
			onlineStatus,
			sw,
			db,
			kv,
		};

		getIssues(props)
			.then((issues) => {
				console.log(issues);
				setIssues(issues);
			})
			.catch(setError)
			.finally(() => {
				setIsLoading(false);
			});

		console.log(`ALL THIS IS SETUP`);
	}, [apolloClient, onlineStatus, isLoading, sw, db]);
	return {
		issues,
		isLoading,
		error,
	};
}

async function getIssues(args: GetIssuesArgs): Promise<Array<Issue>> {
	const { onlineStatus, db } = args;
	/*const issues = await getRemoteIssues(args);
    return issues.sort(sortReleaseDate);*/
	if (onlineStatus) {
		const issues = await getRemoteIssues(args);
		//kv.set('issues', clone(issues), null);
		// await db.saveIssues(issues);
		return issues.sort(sortIssuesReleaseDate);
	}

	return (await db.getIssues()).sort(sortIssuesReleaseDate);
}

async function getRemoteIssues(args: GetIssuesArgs) {
	const { apolloClient, appKey, sw, kv } = args;
	const fetchPolicyKey = 'remote-issues';

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getAllIssues ?? undefined,
		);
	}

	const headers = {
		'app-key': appKey,
		Authorization: getAppToken(),
		'app-user-role': getUserRole(),
	};

	const result = await apolloClient.query({
		query: GET_ISSUES,
		fetchPolicy,
		headers,
	});

	let { issues } = clone(result.data);
	if (issues['__typename']) {
		delete issues['__typename'];
	}

	// TODO Remove this to support features
	issues = issues.map(mapIssuesNonImplementedFeatures);

	if (sw) {
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sw.postMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}

	return issues;
}

export const GET_ISSUES = gql`
	{
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			isPurchased
			releaseDate
		}
	}
`;
